import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, ChevronRight, Bot, User, Globe, ArrowLeft, Sparkles, Wrench } from "lucide-react";
import { matchQuery, CHATBOT_CATEGORIES, ChatKnowledgeEntry, KNOWLEDGE_BASE, detectLanguage } from "@/data/chatbotKnowledge";
import { Link, useLocation } from "react-router-dom";
import { useSettingsStore } from "@/data/settingsStore";

// ─── Chatbot enabled/disabled (admin toggle via settingsStore) ────────────────
export const CHATBOT_ENABLED_KEY = "kaynin_chatbot_enabled";
export const CHATBOT_PATHS_KEY = "kaynin_chatbot_paths";

export function isChatbotEnabled(): boolean {
  return useSettingsStore.getState().getSetting(CHATBOT_ENABLED_KEY, true) as boolean;
}
export function setChatbotEnabled(enabled: boolean) {
  useSettingsStore.getState().updateSetting(CHATBOT_ENABLED_KEY, enabled);
}
export function getChatbotPaths(): string[] {
  return useSettingsStore.getState().getSetting(CHATBOT_PATHS_KEY, ['/']) as string[];
}
export function setChatbotPaths(paths: string[]) {
  useSettingsStore.getState().updateSetting(CHATBOT_PATHS_KEY, paths);
}

// ─── Types ───────────────────────────────────────────────────────────────────
type Lang = "fr" | "ar" | "ar-latn";

interface ChatMessage {
  id: string;
  role: "user" | "bot";
  text: string;
  lang: Lang;
  links?: { label: string; labelAr?: string; labelArLatn?: string; url: string }[];
  suggestions?: string[];
  timestamp: Date;
}

// ─── Quick suggestions by language ───────────────────────────────────────────
const QUICK_SUGGESTIONS: Record<Lang, string[]> = {
  fr: [
    "Qui peut voter ?",
    "Quota femmes ?",
    "Conditions candidature ?",
    "Date des élections ?",
    "C'est quoi la تزكية ?",
    "Comment s'inscrire ?",
  ],
  ar: [
    "شكون يقدر يصوت؟",
    "كوطا النساء؟",
    "شروط الترشح؟",
    "إمتى الانتخابات؟",
    "شنو هي التزكية؟",
    "كيفاش نتسجل؟",
  ],
  "ar-latn": [
    "Chkon y9dar ysowwet?",
    "Quota nsa?",
    "Chorot tarachoh?",
    "Imta l'intikhabat?",
    "Chno hiya tazkiya?",
    "Kifach ntsajel?",
  ],
};

const GREETING_MSG = "👋 مرحباً! Azul! Bonjour! Marhaba!\n\nأنا **كاينبوت**، المساعد ديالكم للانتخابات 2026.\nJe suis **Kayn'Bot**, votre assistant pour les élections.\nAna **Kayn'Bot**, l'moussa3id dyalkoum l'intikhabat.\n\nكيفاش نقدر نعاونك؟ Comment puis-je vous aider ? Kifach n9dar n3awnak?";

const GREETINGS: Record<Lang, string> = {
  fr: GREETING_MSG,
  ar: GREETING_MSG,
  "ar-latn": GREETING_MSG,
};

const NO_MATCH: Record<Lang, string> = {
  fr: "🤔 Je n'ai pas trouvé de réponse exacte. Essayez de reformuler ou choisissez un sujet.\n\nVous pouvez aussi consulter les sites officiels :\n• **listeselectorales.ma** — Inscription listes électorales\n• **sgg.gov.ma** — Textes de loi\n• **maroc.ma** — Portail du gouvernement",
  ar: "🤔 ما لقيتش جواب دقيق. حاول تعاود صوغ السؤال ولا ختار موضوع.\n\nتقدر تشوف المواقع الرسمية:\n• **listeselectorales.ma** — التسجيل فـ اللوائح\n• **sgg.gov.ma** — النصوص القانونية\n• **maroc.ma** — بوابة الحكومة",
  "ar-latn": "🤔 Ma l9itch jawab d9i9. 7awel t3awed tsiygh so2al ola khtar mawdo3.\n\nT9dar tchof lmawa9i3 rasmiya:\n• **listeselectorales.ma** — Tassjil f lawa2i7\n• **sgg.gov.ma** — Nnosos lqanoniya\n• **maroc.ma** — Bawabat l7oukouma",
};

// ─── Simple markdown-like renderer ───────────────────────────────────────────
function renderText(text: string) {
  return text.split("\n").map((line, i) => {
    // Bold
    const parts = line.split(/\*\*(.*?)\*\*/g);
    const rendered = parts.map((part, j) =>
      j % 2 === 1 ? <strong key={j} className="font-bold text-foreground">{part}</strong> : part
    );
    return (
      <span key={i} className="block">
        {rendered}
      </span>
    );
  });
}

// ─── Main Component ──────────────────────────────────────────────────────────
// ─── Disabled message (friendly maintenance) ────────────────────────────────
const DISABLED_MESSAGE = {
  fr: "🛠️ **Kayn'Bot** fait une petite pause !\n\nNotre assistant est en cours d'amélioration pour mieux vous servir. Il sera de retour très bientôt, inchallah ! 😊\n\nEn attendant, n'hésitez pas à consulter les rubriques du site.",
  ar: "🛠️ **كاينبوت** دار وقفة صغيرة!\n\nالمساعد ديالنا كيتحسّن باش يخدمكم حسن. غادي يرجع قريبا، إن شاء الله! 😊\n\nمابقاش بزاف، تصبّرو معانا.",
};

export default function ChatBotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [allowedPaths, setAllowedPaths] = useState<string[]>(['/']);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [showCategories, setShowCategories] = useState(false);
  const [pulse, setPulse] = useState(true);
  // Track last detected language for UI direction, default to French
  const [lastLang, setLastLang] = useState<Lang>("fr");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const location = useLocation();

  // Check chatbot enabled state and paths
  useEffect(() => {
    setEnabled(isChatbotEnabled());
    setAllowedPaths(getChatbotPaths());
    const handler = () => {
      setEnabled(isChatbotEnabled());
      setAllowedPaths(getChatbotPaths());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // Re-check on open
  useEffect(() => {
    if (isOpen) setEnabled(isChatbotEnabled());
  }, [isOpen]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && enabled) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, enabled]);

  // Init greeting
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          id: "greeting",
          role: "bot",
          text: GREETINGS.fr,
          lang: "fr",
          suggestions: QUICK_SUGGESTIONS.fr,
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen]);

  // Stop pulsing after open
  useEffect(() => {
    if (isOpen) setPulse(false);
  }, [isOpen]);

  const addBotMessage = (
    text: string,
    lang: Lang,
    links?: { label: string; labelAr?: string; labelArLatn?: string; url: string }[],
    suggestions?: string[]
  ) => {
    setMessages(prev => [
      ...prev,
      {
        id: `bot-${Date.now()}`,
        role: "bot",
        text,
        lang,
        links,
        suggestions,
        timestamp: new Date(),
      },
    ]);
  };

  const handleSend = (text?: string) => {
    const query = (text || input).trim();
    if (!query) return;

    // Auto-detect language of the query
    const queryLang = detectLanguage(query);
    setLastLang(queryLang);

    // Add user message
    setMessages(prev => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        role: "user",
        text: query,
        lang: queryLang,
        timestamp: new Date(),
      },
    ]);
    setInput("");

    // Find matches and respond in the detected language
    setTimeout(() => {
      const results = matchQuery(query);

      if (results.length > 0) {
        const best = results[0];
        const answer = queryLang === "ar" ? best.answerAr : queryLang === "ar-latn" ? best.answerArLatn : best.answerFr;
        const followUpSuggestions = results.length > 1
          ? results.slice(1).map(r => queryLang === "ar" ? r.questionAr : queryLang === "ar-latn" ? r.questionArLatn : r.questionFr)
          : QUICK_SUGGESTIONS[queryLang].slice(0, 3);
        addBotMessage(answer, queryLang, best.links, followUpSuggestions);
      } else {
        addBotMessage(NO_MATCH[queryLang], queryLang, [
          { label: "listeselectorales.ma", labelAr: "لوائح الانتخابات", labelArLatn: "lawa2i7 l'intikhabat", url: "https://www.listeselectorales.ma" },
          { label: "Portail Maroc.ma", labelAr: "بوابة المغرب", labelArLatn: "Bawabat lmaghrib", url: "https://www.maroc.ma" },
        ], QUICK_SUGGESTIONS[queryLang].slice(0, 4));
      }
    }, 400 + Math.random() * 300);
  };

  const handleCategoryClick = (categoryId: string) => {
    const entries = KNOWLEDGE_BASE.filter(e => e.category === categoryId);
    if (entries.length === 0) return;

    const questions = entries.map(e => lastLang === "ar" ? e.questionAr : lastLang === "ar-latn" ? e.questionArLatn : e.questionFr);
    const catLabel = CHATBOT_CATEGORIES.find(c => c.id === categoryId);
    
    let title = "";
    if (lastLang === "ar") {
      title = `📂 **${catLabel?.labelAr}**\n\nختارو سؤال:`;
    } else if (lastLang === "ar-latn") {
      title = `📂 **${catLabel?.labelArLatn}**\n\nkhtaro so2al:`;
    } else {
      title = `📂 **${catLabel?.labelFr}**\n\nChoisissez une question :`;
    }

    addBotMessage(title, lastLang, undefined, questions);
    setShowCategories(false);
  };

  const handleReset = () => {
    setMessages([
      {
        id: "greeting-reset",
        role: "bot",
        text: GREETINGS.fr,
        lang: "fr",
        suggestions: QUICK_SUGGESTIONS.fr,
        timestamp: new Date(),
      },
    ]);
    setShowCategories(false);
    setLastLang("fr");
  };

  // Determine the current direction based on the last message's language
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isRtl = lastMessage?.lang === "ar" || lastLang === "ar";

  // Afficher uniquement sur les pages autorisées
  if (!allowedPaths.includes(location.pathname)) return null;

  return (
    <>
      {/* ── Floating Bubble ── */}
      <AnimatePresence>
        {!isOpen && enabled && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsOpen(true)}
            className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-gradient-to-br from-primary via-indigo-500 to-violet-600 text-white shadow-2xl shadow-primary/40 flex items-center justify-center transition-shadow hover:shadow-primary/60 ${pulse ? "animate-bounce" : ""}`}
            aria-label="Ouvrir le chatbot"
          >
            <MessageCircle className="w-7 h-7" />
            {/* Notification dot */}
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 rounded-full flex items-center justify-center text-[9px] font-bold animate-pulse border-2 border-white">
              ?
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* ── Chat Window ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.9 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-4rem)] flex flex-col rounded-3xl overflow-hidden shadow-2xl shadow-black/20 border border-border bg-card"
          >
            {/* ── Header ── */}
            <div className="relative bg-gradient-to-r from-primary via-indigo-500 to-violet-600 px-4 py-3 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-display font-bold text-sm leading-tight flex items-center gap-1.5">
                    Kayn'Bot <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
                  </h3>
                  <p className="text-white/70 text-[10px] font-body">
                    🇲🇦 FR / دارجة / ⵜⴰⵎⴰⵣⵉⵖⵜ — Auto-détection
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  {/* Categories */}
                  <button
                    onClick={() => setShowCategories(!showCategories)}
                    className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors"
                    title="Catégories"
                  >
                    <Globe className="w-4 h-4" />
                  </button>
                  {/* Close */}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* ── Category pills (expandable) ── */}
              <AnimatePresence>
                {showCategories && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-2 overflow-hidden"
                  >
                    <div className="flex flex-wrap gap-1.5 pb-1">
                      {CHATBOT_CATEGORIES.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => handleCategoryClick(cat.id)}
                          className="px-2.5 py-1 rounded-full bg-white/20 hover:bg-white/30 text-white text-[10px] font-body font-semibold transition-colors whitespace-nowrap"
                        >
                          {lastLang === "ar" ? cat.labelAr : lastLang === "ar-latn" ? cat.labelArLatn : cat.labelFr}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* ── Messages or Disabled state ── */}
            {!enabled ? (
              /* ── Friendly disabled / maintenance message ── */
              <div className="flex-1 flex flex-col items-center justify-center p-6 bg-gradient-to-b from-muted/20 to-background text-center">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", damping: 20, stiffness: 200 }}
                  className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4 shadow-lg shadow-orange-200"
                >
                  <Wrench className="w-7 h-7 text-white" />
                </motion.div>
                <div className="rounded-2xl bg-card border border-border px-5 py-4 shadow-sm max-w-[300px]">
                  <div className="text-[13px] font-body leading-relaxed text-foreground">
                    {renderText(DISABLED_MESSAGE.fr)}
                  </div>
                  <div className="mt-3 pt-3 border-t border-border text-[13px] font-body leading-relaxed text-foreground" dir="rtl">
                    {renderText(DISABLED_MESSAGE.ar)}
                  </div>
                </div>
              </div>
            ) : (
            <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gradient-to-b from-muted/20 to-background scroll-smooth">
              {messages.map(msg => {
                const msgRtl = msg.lang === "ar";
                return (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.2 }}
                    className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                    dir={msgRtl ? "rtl" : "ltr"}
                  >
                    {/* Avatar */}
                    {msg.role === "bot" && (
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-violet-600 flex items-center justify-center shrink-0 mt-0.5">
                        <Bot className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    {msg.role === "user" && (
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shrink-0 mt-0.5">
                        <User className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}

                    <div className="max-w-[85%]">
                      {/* Message bubble */}
                      <div
                        className={`rounded-2xl px-3.5 py-2.5 text-[13px] font-body leading-relaxed ${
                          msg.role === "user"
                            ? `bg-primary text-white ${msgRtl ? "rounded-tl-md" : "rounded-tr-md"}`
                            : `bg-card border border-border text-foreground ${msgRtl ? "rounded-tr-md" : "rounded-tl-md"} shadow-sm`
                        }`}
                        dir={msgRtl ? "rtl" : "ltr"}
                      >
                        {renderText(msg.text)}
                      </div>

                      {/* Links */}
                      {msg.links && msg.links.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {msg.links.map(link => {
                            let linkLabel = link.label;
                            if (msg.lang === "ar" && link.labelAr) linkLabel = link.labelAr;
                            if (msg.lang === "ar-latn" && link.labelArLatn) linkLabel = link.labelArLatn;
                            const isExternal = link.url.startsWith("http");
                            if (isExternal) {
                              return (
                                <a
                                  key={link.url}
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-[11px] font-body font-semibold hover:bg-primary/20 transition-colors border border-primary/20"
                                >
                                  <ChevronRight className="w-3 h-3" />
                                  {linkLabel}
                                </a>
                              );
                            }
                            return (
                              <Link
                                key={link.url}
                                to={link.url}
                                onClick={() => setIsOpen(false)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-[11px] font-body font-semibold hover:bg-primary/20 transition-colors border border-primary/20"
                              >
                                <ChevronRight className="w-3 h-3" />
                                {linkLabel}
                              </Link>
                            );
                          })}
                        </div>
                      )}

                      {/* Suggestions */}
                      {msg.suggestions && msg.suggestions.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {msg.suggestions.map((s, i) => (
                            <button
                              key={i}
                              onClick={() => handleSend(s)}
                              className="px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 text-foreground text-[11px] font-body font-medium transition-colors border border-border hover:border-primary/30 text-start"
                              dir={detectLanguage(s) === "ar" ? "rtl" : "ltr"}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            )}

            {/* ── Footer / Input ── */}
            {enabled && (
            <div className="shrink-0 border-t border-border bg-card p-3">
              <form
                onSubmit={e => { e.preventDefault(); handleSend(); }}
                className="flex items-center gap-2"
              >
                <button
                  type="button"
                  onClick={handleReset}
                  className="w-8 h-8 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
                  title="Recommencer / ابدأ من جديد"
                >
                  <ArrowLeft className={`w-4 h-4 ${isRtl ? "rotate-180" : ""}`} />
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Posez votre question / سوّل سؤالك..."
                  className="flex-1 h-10 rounded-xl border border-input bg-background px-3 py-2 text-sm font-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 placeholder:text-muted-foreground/60"
                  dir="auto"
                />
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={!input.trim()}
                  className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-violet-600 text-white flex items-center justify-center shadow-md shadow-primary/30 disabled:opacity-40 disabled:shadow-none transition-all shrink-0"
                >
                  <Send className="w-4 h-4" />
                </motion.button>
              </form>
              <p className="text-[9px] text-center text-muted-foreground/50 mt-1.5 font-body">
                Kayn'Bot · Sources : Loi n° 27-11 · Loi n° 29-11 · listeselectorales.ma
              </p>
            </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
